/**
 * Свой выбор даты и времени вместо системного (16.10.2026,
 * docs/design/date-2026-10-16).
 *
 * Системный выбор iPhone — барабан или календарь Safari — не похож на SKOKA
 * и закрывает форму целиком. Здесь всё своё: сетка месяца (сегодня — в
 * кольце, выбранный день — залит), месяц и год — барабанами по тапу на
 * «Октябрь 2026», время — барабанами часов и минут.
 *
 * В записи операции календарь раскрывается прямо под строкой «Когда»
 * (`WhenPanel`) и сворачивается, когда день выбран или человек перешёл к
 * другому полю. В остальных формах поле даты открывает тот же календарь в
 * листе снизу (`DateButton`): выбрал день — лист уехал.
 */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from './icon.js';
import { SheetWrap } from './sheet.js';

const MONTHS = ['Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь', 'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'];
const MONTHS_OF = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'];
const WEEK = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

const pad2 = (n: number) => String(n).padStart(2, '0');
const iso = (y: number, m: number, d: number) => `${y}-${pad2(m + 1)}-${pad2(d)}`;
const parts = (s: string) => ({ y: Number(s.slice(0, 4)), m: Number(s.slice(5, 7)) - 1, d: Number(s.slice(8, 10)) });
const localToday = (): string => { const t = new Date(); return iso(t.getFullYear(), t.getMonth(), t.getDate()); };
/** Месяц как число для сравнений: 2026-10 → 24321. */
const monthNo = (y: number, m: number) => y * 12 + m;

/** «16 октября 2026» — для поля даты в формах. */
export function dateText(value: string, today = localToday()): string {
  if (!/^\d{4}-\d{2}-\d{2}/.test(value)) return '';
  const { y, m, d } = parts(value);
  return `${d} ${MONTHS_OF[m]}${y === parts(today).y ? '' : ` ${y}`}`;
}

// ───────────────────────────────────────────────────────────── барабан

const ROW = 32;

/**
 * Барабан: прокручиваемый столбец, выбранное — по центру. Недоступное
 * значение видно, но барабан к нему не останавливается — доезжает до
 * ближайшего доступного.
 */
function Wheel({ items, index, onIndex, off, wide = false, label }: {
  items: readonly string[];
  index: number;
  onIndex: (i: number) => void;
  off?: (i: number) => boolean;
  wide?: boolean;
  label: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [cur, setCur] = useState(index);
  const timer = useRef(0);
  const latest = useRef({ onIndex, off, items });
  latest.current = { onIndex, off, items };
  useLayoutEffect(() => {
    const el = ref.current;
    if (el && Math.round(el.scrollTop / ROW) !== index) el.scrollTop = index * ROW;
    setCur(index);
  }, [index]);
  const settle = () => {
    const el = ref.current;
    if (!el) return;
    const { onIndex: pick, off: isOff, items: list } = latest.current;
    let i = Math.max(0, Math.min(list.length - 1, Math.round(el.scrollTop / ROW)));
    if (isOff?.(i)) {
      // До ближайшего доступного — сначала назад, к прошлому.
      let j = i;
      while (j > 0 && isOff(j)) j--;
      if (isOff(j)) { j = i; while (j < list.length - 1 && isOff(j)) j++; }
      i = j;
    }
    if (Math.abs(el.scrollTop - i * ROW) > 1) el.scrollTo({ top: i * ROW, behavior: 'smooth' });
    setCur(i);
    pick(i);
  };
  return (
    <div className={`dpk-wheel${wide ? ' wide' : ''}`} ref={ref} role="listbox" aria-label={label}
      onScroll={() => {
        const el = ref.current;
        if (el) setCur(Math.max(0, Math.min(items.length - 1, Math.round(el.scrollTop / ROW))));
        window.clearTimeout(timer.current);
        timer.current = window.setTimeout(settle, 120);
      }}>
      {items.map((v, i) => (
        <button key={v + i} type="button" role="option" aria-selected={i === cur}
          className={`${i === cur ? 'on' : ''}${off?.(i) ? ' off' : ''}`}
          onClick={() => ref.current?.scrollTo({ top: i * ROW, behavior: 'smooth' })}>{v}</button>
      ))}
    </div>
  );
}

// ───────────────────────────────────────────────────────────── календарь

/**
 * Сетка месяца. `min`/`max` — `ГГГГ-ММ-ДД`; дни вне них недоступны, и
 * листать к месяцам целиком вне них нельзя.
 */
export function Calendar({ value, min, max, onPick }: {
  value: string;
  min?: string | undefined;
  max?: string | undefined;
  onPick: (date: string) => void;
}) {
  const today = localToday();
  const start = parts(/^\d{4}-\d{2}-\d{2}/.test(value) ? value : today);
  const [view, setView] = useState({ y: start.y, m: start.m });
  const [months, setMonths] = useState(false);
  const lo = min ? parts(min) : null;
  const hi = max ? parts(max) : null;
  const before = (y: number, m: number) => !!lo && monthNo(y, m) < monthNo(lo.y, lo.m);
  const after = (y: number, m: number) => !!hi && monthNo(y, m) > monthNo(hi.y, hi.m);
  const prev = view.m === 0 ? { y: view.y - 1, m: 11 } : { y: view.y, m: view.m - 1 };
  const next = view.m === 11 ? { y: view.y + 1, m: 0 } : { y: view.y, m: view.m + 1 };
  const first = (new Date(view.y, view.m, 1).getDay() + 6) % 7;
  const count = new Date(view.y, view.m + 1, 0).getDate();
  const out = (d: string) => (!!min && d < min) || (!!max && d > max);
  // Годы барабана — от границ поля, без них — десять лет в обе стороны.
  const y0 = lo ? lo.y : parts(today).y - 10;
  const y1 = hi ? hi.y : parts(today).y + 10;
  const years = Array.from({ length: y1 - y0 + 1 }, (_, i) => String(y0 + i));
  return (
    <div className="dpk">
      <div className="dpk-head">
        <button type="button" className={`dpk-month${months ? ' open' : ''}`} aria-expanded={months}
          onClick={() => setMonths((v) => !v)}>
          {MONTHS[view.m]} {view.y}<span className="dpk-mchev"><Icon name="chevron-down" size={11} /></span>
        </button>
        {!months && (
          <span className="dpk-nav">
            <button type="button" aria-label={`${MONTHS[prev.m]} ${prev.y}`} disabled={before(prev.y, prev.m)}
              onClick={() => setView(prev)}><Icon name="chevron-left" size={14} /></button>
            <button type="button" aria-label={`${MONTHS[next.m]} ${next.y}`} disabled={after(next.y, next.m)}
              onClick={() => setView(next)}><Icon name="chevron-right" size={14} /></button>
          </span>
        )}
      </div>
      {months ? (
        <div className="dpk-wheels tall">
          <div className="dpk-band wide" />
          <Wheel label="Месяц" wide items={MONTHS} index={view.m}
            off={(i) => before(view.y, i) || after(view.y, i)}
            onIndex={(i) => setView((v) => ({ ...v, m: i }))} />
          <Wheel label="Год" items={years} index={Math.max(0, years.indexOf(String(view.y)))}
            off={(i) => before(y0 + i, 11) || after(y0 + i, 0)}
            onIndex={(i) => setView((v) => {
              // Сменили год — месяц остаётся в его границах.
              const y = y0 + i;
              const m = before(y, v.m) ? lo!.m : after(y, v.m) ? hi!.m : v.m;
              return { y, m };
            })} />
        </div>
      ) : (
        <>
          <div className="dpk-week">{WEEK.map((w) => <span key={w}>{w}</span>)}</div>
          <div className="dpk-days">
            {Array.from({ length: first }, (_, i) => <span key={`b${i}`} />)}
            {Array.from({ length: count }, (_, i) => {
              const d = iso(view.y, view.m, i + 1);
              return (
                <button key={d} type="button" disabled={out(d)}
                  className={`dpk-day${d === value ? ' on' : ''}${d === today ? ' today' : ''}`}
                  aria-label={`${i + 1} ${MONTHS_OF[view.m]} ${view.y}`} aria-pressed={d === value}
                  onClick={() => onPick(d)}>{i + 1}</button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

/** Время: кнопка «10:23»; тап — барабаны часов и минут под ней. */
export function TimeRow({ value, onChange }: { value: string; onChange: (time: string) => void }) {
  const [open, setOpen] = useState(false);
  const h = Number(value.slice(0, 2)) || 0;
  const m = Number(value.slice(3, 5)) || 0;
  const hours = Array.from({ length: 24 }, (_, i) => pad2(i));
  const mins = Array.from({ length: 60 }, (_, i) => pad2(i));
  return (
    <>
      <div className={`dpk-time${open ? ' open' : ''}`}>
        <span>Время</span>
        <button type="button" className="dpk-timepill" aria-expanded={open} onClick={() => setOpen((v) => !v)}>{value}</button>
      </div>
      <Collapse open={open} reveal>
        <div className="dpk-wheels">
          <div className="dpk-band" />
          <Wheel label="Часы" items={hours} index={h} onIndex={(i) => onChange(`${pad2(i)}:${pad2(m)}`)} />
          <b>:</b>
          <Wheel label="Минуты" items={mins} index={m} onIndex={(i) => onChange(`${pad2(h)}:${pad2(i)}`)} />
        </div>
      </Collapse>
    </>
  );
}

/** «Уменьшить движение» в системе — без плавной прокрутки. */
const calm = (): boolean => typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Плавно раскрыть и свернуть блок по высоте. Свёрнутое остаётся в разметке,
 * но не нажимается и не читается диктором. `reveal` — раскрывшись,
 * докрутить форму, чтобы блок был виден целиком.
 */
export function Collapse({ open, children, className, reveal = false }: {
  open: boolean; children: ReactNode; className?: string; reveal?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => { if (ref.current) ref.current.inert = !open; }, [open]);
  useEffect(() => {
    if (!open || !reveal) return;
    // После раскрытия: пока блок растёт, докручивать не к чему.
    const t = window.setTimeout(() => ref.current?.scrollIntoView?.({ block: 'nearest', behavior: calm() ? 'auto' : 'smooth' }), 300);
    return () => window.clearTimeout(t);
  }, [open, reveal]);
  return (
    <div ref={ref} className={`collapse${open ? ' open' : ''}${className ? ` ${className}` : ''}`} aria-hidden={!open}>
      <div className="collapse-in">{children}</div>
    </div>
  );
}

// ─────────────────────────────────────────────── «Когда» в записи операции

/**
 * Строка «Когда» и календарь под ней. Тап по строке раскрывает и сворачивает
 * календарь; он сворачивается и сам — когда день выбран или человек нажал
 * другое поле формы. Раскрыт он или нет, решает форма: пока он открыт, она
 * убирает свою клавиатуру.
 */
export function WhenField({ open, onOpen, text, date, time, max, onDate, onTime, label }: {
  open: boolean;
  onOpen: (open: boolean) => void;
  /** «Сегодня, 10:23». */
  text: string;
  date: string;
  time: string;
  /** Позже этого дня выбрать нельзя: операция уже случилась. */
  max: string;
  onDate: (date: string) => void;
  onTime: (time: string) => void;
  /** Для диктора: «Дата и время операции». */
  label: string;
}) {
  const row = useRef<HTMLButtonElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const close = useRef(() => onOpen(false));
  close.current = () => onOpen(false);
  /*
   * Календарь остаётся в разметке и свёрнутым — иначе сворачивать было бы
   * нечего, он пропадал бы разом. А каждое новое раскрытие начинается заново:
   * с месяца выбранного дня и закрытым временем.
   */
  const round = useRef(0);
  const was = useRef(open);
  if (open && !was.current) round.current += 1;
  was.current = open;
  useEffect(() => {
    if (!open) return;
    // Нажатие, а не касание: палец, которым листают форму, календарь не закрывает.
    const away = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t || box.current?.contains(t) || row.current?.contains(t)) return;
      close.current();
    };
    document.addEventListener('click', away, true);
    return () => document.removeEventListener('click', away, true);
  }, [open]);
  return (
    <>
      <button ref={row} type="button" className={`oprow whenrow${open ? ' open' : ''}`} aria-expanded={open}
        aria-label={`${label}: ${text}`} onClick={() => onOpen(!open)}>
        <span>Когда</span>
        <span className="opval plain">{text}<span className="whenchev" aria-hidden="true"><Icon name="chevron-down" size={12} /></span></span>
      </button>
      <Collapse open={open} className="whenpanel" reveal>
        <div ref={box}>
          <Calendar key={`d${round.current}`} value={date} max={max} onPick={(d) => { onDate(d); onOpen(false); }} />
          <TimeRow key={`t${round.current}`} value={time} onChange={onTime} />
        </div>
      </Collapse>
    </>
  );
}

// ─────────────────────────────────────────────── поле даты в остальных формах

/**
 * Поле даты: видимое значение, а по тапу — тот же календарь в листе снизу.
 * `overlay` — прозрачная кнопка поверх строки, где значение уже написано
 * (на месте прежнего системного поля); `children` — своя кнопка, например
 * чип; иначе — поле в рамке, как ввод.
 */
export function DateButton({ value, onChange, min, max, label, overlay = false, time, onTime, placeholder = 'Выбрать', clear, className, children }: {
  value: string;
  onChange: (date: string) => void;
  min?: string | undefined;
  max?: string | undefined;
  label: string;
  overlay?: boolean;
  /** Время рядом с днём — у операций из чека и выписки. */
  time?: string;
  onTime?: (time: string) => void;
  placeholder?: string;
  /**
   * Дата необязательна: под календарём — как остаться без неё («Не указана»,
   * «Не договорились»). У системного поля дату можно было стереть.
   */
  clear?: string;
  className?: string;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const cls = className ? ` ${className}` : '';
  return (
    <>
      {overlay
        ? <button type="button" className={`opover overbtn${cls}`} aria-label={label} onClick={() => setOpen(true)} />
        : children !== undefined
          ? <button type="button" className={className} aria-label={label} onClick={() => setOpen(true)}>{children}</button>
          : <button type="button" className={`fieldbtn${value ? '' : ' empty'}${cls}`} aria-label={label}
            onClick={() => setOpen(true)}><span>{value ? dateText(value) : placeholder}</span><Icon name="calendar" size={15} /></button>}
      {open && (
        <SheetWrap title={label} onClose={() => setOpen(false)} className="datesheet">
          <Calendar value={value} min={min} max={max} onPick={(d) => { onChange(d); setOpen(false); }} />
          {time !== undefined && onTime && <TimeRow value={time} onChange={onTime} />}
          {clear && value && (
            <button type="button" className="dpk-clear" onClick={() => { onChange(''); setOpen(false); }}>{clear}</button>
          )}
        </SheetWrap>
      )}
    </>
  );
}
